export type TemplateSection = {
  key: string;
  title: string;
};

export type TemplateSectionEditorRow = {
  id: string;
  key: string;
  title: string;
  /** When true, key is kept on save (loaded from an existing template). */
  preserveKey: boolean;
};

let editorRowIdCounter = 0;

export function nextEditorRowId(): string {
  editorRowIdCounter += 1;
  return `template-section-${editorRowIdCounter}`;
}

export function slugifySectionKey(title: string): string {
  return (
    title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 64) || "section"
  );
}

function uniquifySectionKey(base: string, used: Set<string>): string {
  if (!used.has(base)) return base;
  let n = 2;
  while (used.has(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
}

export function parseTemplateStructure(structure: unknown): TemplateSection[] {
  if (!structure || typeof structure !== "object") return [];
  const sections = (structure as { sections?: unknown }).sections;
  if (!Array.isArray(sections)) return [];

  return sections
    .map((item) => {
      if (!item || typeof item !== "object") return null;
      const row = item as Record<string, unknown>;
      const key = typeof row.key === "string" ? row.key.trim() : "";
      const title = typeof row.title === "string" ? row.title : "";
      if (!title.trim() && !key) return null;
      return { key, title: title.trim() || key };
    })
    .filter((item): item is TemplateSection => item !== null);
}

export function parseStructureJson(text: string): TemplateSection[] {
  try {
    return parseTemplateStructure(JSON.parse(text) as unknown);
  } catch {
    return [];
  }
}

export function templateSectionsToEditorRows(sections: TemplateSection[]): TemplateSectionEditorRow[] {
  if (sections.length === 0) {
    return [createEmptyEditorRow()];
  }
  return sections.map((section) => ({
    id: nextEditorRowId(),
    key: section.key,
    title: section.title,
    preserveKey: Boolean(section.key),
  }));
}

export function createEmptyEditorRow(): TemplateSectionEditorRow {
  return {
    id: nextEditorRowId(),
    key: "",
    title: "",
    preserveKey: false,
  };
}

export function sectionsForSave(rows: TemplateSectionEditorRow[]): TemplateSection[] {
  const result: TemplateSection[] = [];
  const usedKeys = new Set<string>();

  for (const row of rows) {
    const title = row.title.trim();
    if (!title) continue;

    let key: string;
    if (row.preserveKey && row.key.trim()) {
      key = row.key.trim();
      if (usedKeys.has(key)) {
        key = uniquifySectionKey(key, usedKeys);
      }
    } else {
      key = uniquifySectionKey(slugifySectionKey(title), usedKeys);
    }

    usedKeys.add(key);
    result.push({ key, title });
  }

  return result;
}

export function buildTemplateStructure(sections: TemplateSection[]): { sections: TemplateSection[] } {
  return { sections };
}

export function structureToJsonString(structure: unknown): string {
  const sections = parseTemplateStructure(structure);
  return JSON.stringify(buildTemplateStructure(sections), null, 2);
}

export function formatSectionsForDisplay(structure: unknown): string {
  const sections = parseTemplateStructure(structure);
  if (sections.length === 0) return "—";
  return sections.map((section, index) => `${index + 1}. ${section.title}`).join("\n");
}
